import { Button, Heading, StackProps, Text, VStack } from "@chakra-ui/react"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { captureException } from "@sentry/browser"
import classnames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary"
import LoadingChunkErrorBoundary from "./LoadingChunkErrorBoundary"

export type ErrorBoundaryProps = {
  logToSentry?: boolean
  renderError?: (
    error: Error,
    resetErrorBoundary: () => void
  ) => React.ReactNode
  className?: string
  children: React.ReactNode
  catchLoadingChunkFailures?: boolean
  schoolName?: string
} & StackProps

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  logToSentry = true,
  renderError,
  className,
  children,
  catchLoadingChunkFailures = true,
  schoolName,
  ...stackProps
}) => {
  const refreshPage = () => {
    window.location.assign(window.location.href)
  }

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    if (logToSentry) {
      captureException(error)
    }
  }

  const ErrorFallback = ({
    error,
    resetErrorBoundary
  }: {
    error: Error
    resetErrorBoundary: () => void
  }) => {
    if (renderError) {
      return renderError(error, resetErrorBoundary)
    }

    // Hide the error details from end users in production.
    // Showing the actual error message is poor UX.
    const errorDetails =
      process.env.NODE_ENV === "production" ? null : error.toString()

    return (
      <VStack
        className={classnames("ErrorBoundary", className)}
        w="100%"
        minH="100%"
        maxH="100vh"
        // HACK ALERT: somewhere, the Stack styles could be overridden to display block... (they are).
        display="flex !important"
        justifyContent="center"
        alignItems="center"
        bg="white"
        p={4}
        gap={4}
        borderRadius="20px"
        {...stackProps}
      >
        <Pathicon icon="computer-frown" style={{ fontSize: "2em" }} />
        <Heading
          as="h1"
          // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
          size="h4"
          m={0}
        >
          An unexpected error occurred
        </Heading>
        {!!errorDetails && (
          <Text
            fontFamily="monospace"
            textAlign="left"
            m={0}
            p={4}
            borderRadius="md"
            color="black"
            bg="gray.200"
            maxW="min(600px, 100%)"
            overflow="auto"
          >
            {errorDetails}
          </Text>
        )}
        {schoolName ? (
          <>
            <Button colorPalette="brand" variant="solid" cursor="pointer">
              Email {schoolName}
            </Button>

            <Button
              variant="plain"
              p={0}
              bg="none"
              cursor="pointer"
              onClick={refreshPage}
            >
              or refresh
            </Button>
          </>
        ) : (
          <Button
            colorPalette="brand"
            variant="solid"
            cursor="pointer"
            onClick={refreshPage}
          >
            Try refreshing
          </Button>
        )}
      </VStack>
    )
  }

  if (catchLoadingChunkFailures) {
    return (
      <LoadingChunkErrorBoundary className={className}>
        {children}
      </LoadingChunkErrorBoundary>
    )
  }

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={() => {
        // Reset the state of your app here
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}

ErrorBoundary.propTypes = {
  logToSentry: PropTypes.bool,
  catchLoadingChunkFailures: PropTypes.bool,
  renderError: PropTypes.func,
  schoolName: PropTypes.string
}

ErrorBoundary.displayName = "ErrorBoundary"

export default ErrorBoundary
